import React from 'react';

import { css } from '@emotion/css';
import type { DateRangeValue } from '@hopdrive/sdk-ui';

import DriverMap from '@features/moves/map/DriverMap';
import { useDriverMapData } from '@features/moves/map/useDriverMapData';
import type { TabActionsContribution } from '@features/products/actions/types';
import { useShellTabActions } from '@features/products/shell/shellTabActions';

import TeamTabControls from './TeamTabControls';
import { TEAM_DEFAULT_RANGE, buildTeamMapFilters, resolveTeamWindow } from './teamTabFilters';

// COMPONENT ------------------------------------------------------------------ //

/**
 * The tab owns the window and search state, opening on today every visit with nothing persisted,
 * and the map's data hook. The map view is presentational.
 */
const TeamTab = () => {
  const cls = useStyles();

  const [range, setRange] = React.useState<DateRangeValue>(TEAM_DEFAULT_RANGE);
  const [search, setSearch] = React.useState('');

  // Stable ISO bounds avoid a refetch on every render.
  const { start, end } = React.useMemo(() => resolveTeamWindow(range), [range]);
  const additionalFilters = React.useMemo(() => buildTeamMapFilters({ search }), [search]);

  // On the Team tab the map is always the view, so it always fetches.
  const data = useDriverMapData({ start, end, additionalFilters, enabled: true });

  const tabActions = React.useMemo<TabActionsContribution>(() => ({ sync: data.refetch }), [data.refetch]);
  useShellTabActions(tabActions);

  const handleClear = () => {
    setRange(TEAM_DEFAULT_RANGE);
    setSearch('');
  };

  return (
    <div className={cls.layout}>
      <TeamTabControls
        range={range}
        onRangeChange={setRange}
        search={search}
        onSearchChange={setSearch}
        onClear={handleClear}
      />
      <div className={cls.mapContainer}>
        <DriverMap data={data} />
      </div>
    </div>
  );
};

// STYLES --------------------------------------------------------------------- //

const useStyles = () => ({
  layout: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  `,
  mapContainer: css`
    flex: 1;
    min-height: 0;
    overflow: hidden;
  `,
});

// EXPORT --------------------------------------------------------------------- //

export default TeamTab;
